"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateFilterComponent = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const calendar_1 = require("primereact/calendar");
const useIsMobile_1 = require("../../hooks/mobile/useIsMobile");
const datesHelpers_1 = require("../../common/utils/datesHelpers");
const react_i18next_1 = require("react-i18next");
const react_router_dom_1 = require("react-router-dom");
const FilterContext_1 = require("../../context/filters/FilterContext");
const toast_1 = require("primereact/toast");
const locales_1 = require("./locales");
const FiltersComponent_module_scss_1 = __importDefault(require("./FiltersComponent.module.scss"));
const CLEAR_DATE = 'clear';
const SIX_MONTHS_IN_MS = 6 * 30 * 24 * 60 * 60 * 1000;
const DateFilterComponent = ({ handleDate, maxRangeInMonths, quickFilters = [], }) => {
    const [datetime24h, setDateTime24h] = (0, react_1.useState)(datesHelpers_1.getDefaultDateRange);
    const { t, i18n } = (0, react_i18next_1.useTranslation)();
    const location = (0, react_router_dom_1.useLocation)();
    const isMobile = (0, useIsMobile_1.useIsMobile)();
    const { filters, setFilters } = (0, FilterContext_1.useFilters)();
    const calendarRef = (0, react_1.useRef)(null);
    const [maxSelectableDate, setMaxSelectableDate] = (0, react_1.useState)(null);
    const toast = (0, react_1.useRef)(null);
    const [localeReady, setLocaleReady] = (0, react_1.useState)(false);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endToday = new Date();
    endToday.setHours(23, 59, 59, 999);
    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const [quickFilter, setQuickFilter] = (0, react_1.useState)(null);
    const applyQuickFilter = (filter) => {
        const [start, end] = filter.range();
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        setDateTime24h([start, end]);
        setQuickFilter(filter.id);
    };
    (0, react_1.useEffect)(() => {
        const language = i18n.language.startsWith('es') ? 'es-ES' : 'en-US';
        (0, locales_1.configureLocales)(language);
        setLocaleReady(true);
    }, [i18n.language]);
    (0, react_1.useEffect)(() => {
        if (filters.startDate && filters.endDate) {
            const startDate = new Date(filters.startDate);
            const endDate = new Date(filters.endDate);
            setDateTime24h([startDate, endDate]);
        }
        else if (localStorage.getItem('removeDate') === CLEAR_DATE) {
            setDateTime24h(null);
        }
    }, [location]);
    const clearDates = () => {
        const defaultDateRange = (0, datesHelpers_1.getDefaultDateRange)();
        setQuickFilter('none');
        if (defaultDateRange) {
            defaultDateRange[0]?.setHours(0, 0, 0, 0);
            defaultDateRange[1]?.setHours(23, 59, 59, 999);
            handleDate(defaultDateRange);
        }
        setDateTime24h(defaultDateRange);
        setMaxSelectableDate(null);
    };
    const handleCalendarChange = (value) => {
        localStorage.setItem('currentPage', '1');
        setQuickFilter('none');
        if (value && value.length === 2) {
            const [start, end] = value;
            const adjustedStart = start ? new Date(start) : null;
            const adjustedEnd = end ? new Date(end) : null;
            if (adjustedStart) {
                adjustedStart.setHours(0, 0, 0, 0);
            }
            if (adjustedEnd) {
                adjustedEnd.setHours(23, 59, 59, 999);
            }
            const MAX_RANGE_IN_MS = (maxRangeInMonths ?? 6) * 30 * 24 * 60 * 60 * 1000;
            if (adjustedStart) {
                const maxDate = new Date(adjustedStart.getTime() + MAX_RANGE_IN_MS);
                setMaxSelectableDate(maxDate);
            }
            setDateTime24h([adjustedStart, adjustedEnd]);
        }
        else {
            setDateTime24h(value);
        }
    };
    const handleSearch = () => {
        localStorage.setItem('currentPage', '1');
        if (!datetime24h)
            return;
        if (datetime24h[0] === null || datetime24h[1] === null) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: t('selectDateRange'),
                    life: 500,
                });
            }
            return;
        }
        handleDate(datetime24h);
        calendarRef.current?.hide();
    };
    const footerTemplate = () => ((0, jsx_runtime_1.jsxs)("div", { className: "p-2 flex flex-col gap-3", children: [quickFilters?.length > 0 && ((0, jsx_runtime_1.jsx)("div", { className: "flex justify-center gap-2 flex-wrap", children: quickFilters.map((filter) => ((0, jsx_runtime_1.jsx)("span", { className: `${FiltersComponent_module_scss_1.default.quickFilterChip} ${quickFilter === filter.id ? FiltersComponent_module_scss_1.default.active : ''}`, onClick: () => applyQuickFilter(filter), children: filter.label }, filter.id))) })), (0, jsx_runtime_1.jsxs)("div", { className: "border-t pt-2 flex justify-between", children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: "p-button p-button-text", onClick: clearDates, children: t('clear') }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "p-button p-button-sm p-button-primary", onClick: handleSearch, children: t('search') })] })] }));
    if (!localeReady) {
        return null;
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(toast_1.Toast, { className: isMobile ? 'w-3/4 text-xs' : 'w-full', ref: toast }), (0, jsx_runtime_1.jsx)("div", { className: "w-full card flex flex-wrap gap-3 p-fluid", children: (0, jsx_runtime_1.jsxs)("div", { className: "relative w-full flex-auto", children: [(0, jsx_runtime_1.jsx)(calendar_1.Calendar, { appendTo: document.body, id: "calendar-range", "aria-label": "Calendar range", inputStyle: {
                                fontSize: isMobile ? '1rem' : '',
                                padding: isMobile ? '0.5rem' : '',
                                paddingLeft: isMobile ? '0.5rem' : '10px',
                                border: isMobile ? '1px solid #ced4da' : '',
                                borderRadius: isMobile ? '12px' : '',
                            }, style: {
                                height: isMobile ? 'auto' : '1.5rem',
                            }, value: datetime24h, locale: i18n.language.startsWith('es') ? 'es-ES' : 'en-US', ref: calendarRef, placeholder: isMobile ? t('selectDateRange') : 'DD/MM/YYYY - DD/MM/YYYY', onChange: (e) => handleCalendarChange(e.value), footerTemplate: footerTemplate, selectionMode: "range", dateFormat: "dd/mm/yy", readOnlyInput: !isMobile, touchUI: isMobile, maxDate: maxSelectableDate || undefined, className: `w-full customDatePicker ${isMobile ? 'mobile-calendar' : ''}`, icon: "pi pi-calendar" }), datetime24h && datetime24h.length > 0 && ((0, jsx_runtime_1.jsx)("button", { type: "button", className: `${isMobile ? 'mr-5' : 'mr-1'} absolute right-0 mr-1 top-1/2 transform -translate-y-1/2 bg-transparent text-gray-500 rounded-full`, onClick: clearDates, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-times" }) }))] }) })] }));
};
exports.DateFilterComponent = DateFilterComponent;
